import { type DocNode } from '@atlaskit/adf-schema/doc';
import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { type CreationContextParams } from '@atlassian/conversation-assistant-external-context/types';
import { type SendMessageSearchArtifact } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type ContentContext } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import type { ReadContentCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';

import { getChatContextContentType } from './getChatContextContentType';
import { applyCreationContextContract } from './applyCreationContextContract';
import { getCreationContextBlocks } from './getCreationContextBlocks';
import { getCreationContextExperience } from './getCreationContextExperience';
import { handleEditorContextAnalytics } from './handleEditorContextAnalytics';
import type { ChatStoreContainerProps } from './types';

type ChatContextStoreActions = ChatStoreContainerProps['chatContextStoreActions'];

/**
 * Result shape produced by {@link populateSendMessageContext}. Field types are
 * derived directly from the chat-context store getters so the concrete context
 * shapes (e.g. `editorContext.isViewMode`, `editorContext.dynamicUiType`) are
 * preserved for downstream consumers. Made explicit to satisfy
 * `--isolatedDeclarations` now that this helper is exported from its own file.
 */
type PopulateSendMessageContextResult = {
	browserContext: Awaited<ReturnType<ChatContextStoreActions['getBrowserContext']>>;
	editorContext: Awaited<ReturnType<ChatContextStoreActions['getEditorContext']>> | undefined;
	whiteboardContext:
		| Awaited<ReturnType<ChatContextStoreActions['getWhiteboardContext']>>
		| undefined;
	slidesContext: Awaited<ReturnType<ChatContextStoreActions['getSlidesContext']>> | undefined;
	databaseContext: Awaited<ReturnType<ChatContextStoreActions['getDatabaseContext']>> | undefined;
	dashboardEditorContext: Awaited<ReturnType<ChatContextStoreActions['getDashboardEditorContext']>>;
	additionalContext: {
		spaceCreateContext: Awaited<ReturnType<ChatContextStoreActions['getSpaceCreateContext']>>;
		creationContext: Record<string, unknown>;
		userTimezone: string;
		search_artifact: SendMessageSearchArtifact | undefined;
	};
	computedExperience: CreationContextParams['experience'];
	aiFeatureContext: ReturnType<ChatContextStoreActions['getAIFeatureContext']>;
	jiraWorkflowContext: ReturnType<ChatContextStoreActions['getJiraWorkflowContext']>;
	dashboardContext: ReturnType<ChatContextStoreActions['getDashboardContext']>;
	useCurrentBrowsingContext: ReturnType<ChatContextStoreActions['useCurrentBrowsingContext']>;
};

export const populateSendMessageContext = async (
	chatContextStoreActions: ChatStoreContainerProps['chatContextStoreActions'],
	isFirstPrompt: boolean,
	source: string | undefined,
	prompt: DocNode | undefined,
	addCurrentPageAsContextUserPreference: boolean | undefined,
	contentContextValue: ContentContext,
	creationContextParams: CreationContextParams | undefined,
	productKey: string | undefined,
	sendAnalyticsEvent: (payload: AnalyticsEventPayload) => void,
	activeTargetId?: string | number,
	lastAssistantMessageDynamicUiType?: string,
	skipEditorContext?: boolean,
	isRemixAction?: boolean,
	searchArtifact?: SendMessageSearchArtifact,
	additionalContextReceiverId?: ReadContentCommandProperties['receiverId'],
): Promise<PopulateSendMessageContextResult> => {
	// Scoped to Confluence: the contract comes from the Confluence experience routing spec, and
	// Jira runs its own remix-edit rollout with a different payload shape.
	const isCreationContextContractEnabled =
		productKey === 'confluence' && fg('confluence_aifc_experience_rearchitecture');

	// When the gate is on and the user is in the CWR staging-area on the first prompt with no
	// eligible source, getContextRequirements returns false for contentContext='staging-area'
	// (it ignores userPreference). To ensure the draft editor context is always fetched, fetch
	// with contentContext='global' which respects userPreference=true. The effective content
	// context for all other purposes (isStagingArea, experience, etc.) remains 'staging-area'.
	const isStagingAreaFirstPromptFix =
		fg('confluence_aifc_new_live_page_chat_context') &&
		contentContextValue === 'staging-area' &&
		isFirstPrompt;

	const editorContext = skipEditorContext
		? undefined
		: await chatContextStoreActions.getEditorContext({
				isFirstPrompt,
				source,
				// For staging-area first prompts, getContextRequirements ignores userPreference.
				// Use 'global' so userPreference=true is respected and the draft context is fetched.
				contentContext: isStagingAreaFirstPromptFix ? 'global' : contentContextValue,
				addCurrentPageAsContextUserPreference: isStagingAreaFirstPromptFix
					? true
					: !!addCurrentPageAsContextUserPreference,
				activeTargetId,
				prompt,
			});

	const currentTarget = chatContextStoreActions.getTarget(contentContextValue, activeTargetId);

	// If there's no editor context available and the current target CAN
	// provide context, send analytics to track this error state
	if (!editorContext && !skipEditorContext && currentTarget?.canProvideContext) {
		handleEditorContextAnalytics({
			isFirstPrompt,
			source,
			contentContext: contentContextValue,
			userPreference: !!addCurrentPageAsContextUserPreference,
			sendAnalyticsEvent,
			activeTargetId: !!activeTargetId,
			isStagingArea: chatContextStoreActions.getIsStagingArea(),
		});
	}

	const whiteboardContext = await chatContextStoreActions.getWhiteboardContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const slidesContext = await chatContextStoreActions.getSlidesContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const databaseContext = await chatContextStoreActions.getDatabaseContext({
		isFirstPrompt,
		source,
		contentContext: contentContextValue,
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		activeTargetId,
	});

	const dashboardEditorContext = await chatContextStoreActions.getDashboardEditorContext({
		contentContext: contentContextValue,
		activeTargetId,
	});

	const browserContext = await chatContextStoreActions.getBrowserContext({
		addCurrentPageAsContextUserPreference: !!addCurrentPageAsContextUserPreference,
		enabled: true,
	});

	const jiraContext = await (async () => {
		// Only fetch Jira context when in staging area and product is Jira
		// and not the first prompt (refinement messages)
		if (
			!isFirstPrompt &&
			contentContextValue === 'staging-area' &&
			productKey === 'jira' &&
			fg('jira_aifc_enabled')
		) {
			return chatContextStoreActions.getJiraContext?.();
		}
		return undefined;
	})();

	const additionalCreationContext = editorContext?.additionalContext;
	const additionalCreationContextParams = creationContextParams?.additionalContext;
	const bridgeAdditionalContext = additionalContextReceiverId
		? await chatContextStoreActions.getAdditionalContext(additionalContextReceiverId)
		: undefined;
	if (editorContext && editorContext.additionalContext) {
		delete editorContext.additionalContext;
	}
	const useGenericEditorSkill = editorContext?.useGenericEditorSkill;
	if (editorContext && 'useGenericEditorSkill' in editorContext) {
		delete editorContext.useGenericEditorSkill;
	}

	const isViewMode =
		contentContextValue === 'staging-area'
			? !!editorContext?.isViewMode
			: !!(
					editorContext?.isViewMode ||
					whiteboardContext?.isViewMode ||
					slidesContext?.isViewMode ||
					databaseContext?.isViewMode
				);

	// On a Confluence page in view mode, the editor bridge is not active so editorContext
	// is undefined. We detect this case by checking that productKey is 'confluence', that
	// skipEditorContext is false (we did try to fetch context), and that no other Confluence
	// doc-type context (whiteboard, slides, database) is present — meaning this is a page.
	const isConfluencePageViewMode =
		productKey === 'confluence' &&
		!skipEditorContext &&
		!whiteboardContext &&
		!slidesContext &&
		!databaseContext
			? editorContext === undefined
			: false;
	const creationContextIsViewMode = expValEquals('cc-maui-experiment', 'isEnabled', true)
		? creationContextParams?.isViewMode !== undefined
			? creationContextParams.isViewMode
			: isViewMode || isConfluencePageViewMode
		: isViewMode;
	const isDraftLockedForEditing = !!editorContext?.isDraftLockedForEditing;
	const editorContentType = getChatContextContentType({
		editorContext,
		whiteboardContext,
		databaseContext,
		slidesContext,
	});
	const contentMauiId = editorContext?.contentMauiId ?? creationContextParams?.contentMauiId;
	const mediaFileId = editorContext?.mediaFileId ?? creationContextParams?.mediaFileId;
	// The collection id for the selected media node, sent so the backend can resolve the
	// current media's collection when editing an existing image (image-create action).
	// Only populated for the editor/page flow via read-content-command.
	const mediaCollection = editorContext?.mediaCollection;
	const creationContextSource =
		contentMauiId || mediaFileId
			? 'remix'
			: // If source is explicitly passed, use that, otherwise rely on creationContextParams.source which is set at creation entry point (e.g. 'remix', 'ai-first-creation')
				(source ?? creationContextParams?.source);
	const isRemixExperienceContextEnabled =
		expValEquals('rovo_remix_experience_context', 'isEnabled', true) ||
		// `rovo_remix_experience_context` only targets confluence_web; enable the remix
		// experience for the Jira remix-edit path via its own rollout flag.
		(productKey !== 'confluence' && fg('platform_editor_maui_jira_remix_edit'));
	// The context store returns the raw content context; the outbound
	// creationContext experience/contentTypes are attached here when the request is built.
	const creationContextExperience = getCreationContextExperience({
		contentContextValue: contentContextValue,
		isFirstPrompt,
		source,
		creationContextSource: creationContextParams?.source,
		existingExperience: creationContextParams?.experience,
		existingContentTypes: creationContextParams?.contentTypes,
		chatContextContentType: editorContentType,
		isRemixAction,
		isViewMode: creationContextIsViewMode,
		contentMauiId,
		mediaFileId,
		isRemixExperienceContextEnabled,
		isCreationContextContractEnabled,
	});
	// A remix edit must always carry the block being edited and its source, so these cannot stay
	// behind an unrelated experiment once the contract is enforced.
	const isRemixEditContract =
		isCreationContextContractEnabled &&
		(creationContextExperience?.experience ?? creationContextParams?.experience) === 'remix_edit';

	const creationContextBlocks = getCreationContextBlocks({
		existingBlocks: creationContextParams?.blocks,
		experience: creationContextExperience?.experience ?? creationContextParams?.experience,
		source: creationContextSource,
		contentMauiId,
		mediaFileId,
		dynamicUiType: editorContext?.dynamicUiType ?? creationContextParams?.dynamicUiType,
		isRemixExperienceContextEnabled,
		isCreationContextContractEnabled,
	});
	const isFormsSmartCreate =
		contentContextValue === 'staging-area' &&
		chatContextStoreActions.getStagingAreaEntryPoint() === 'jsmFormsRovoCreate' &&
		fg('soju-5536-forms-skill');
	const creationContext = {
		...(creationContextParams ? creationContextParams : {}),
		// When the rearchitecture gate is on and we're fixing a staging-area first-prompt
		// (user opened Rovo chat on a live page they just created via CWR), the staging-area
		// store state is stale. The user is chatting about the page they're editing — not
		// running a CWR create flow — so isStagingArea should be false.
		isStagingArea: isStagingAreaFirstPromptFix ? false : contentContextValue === 'staging-area',
		productKey,
		// When `contentMauiId` or `mediaFileId` is present, we don't need to send the source
		// Ideally we want to set it upstream, but we need to ensure we don't break existing functionality
		source: creationContextSource,
		isViewMode: creationContextIsViewMode,
		isDraftLockedForEditing,
		useGenericEditorSkill,
		additionalContext:
			additionalCreationContext ||
			additionalCreationContextParams ||
			bridgeAdditionalContext ||
			mediaCollection
				? {
						...additionalCreationContext,
						...additionalCreationContextParams,
						...(mediaCollection ? { mediaCollection } : {}),
						...bridgeAdditionalContext,
					}
				: undefined,
		...(jiraContext && { jiraContext }),
		...((expValEquals('cc-maui-experiment', 'isEnabled', true) || isRemixEditContract) && {
			...(editorContext?.dynamicUiSource && {
				dynamicUiSource: {
					...editorContext.dynamicUiSource,
					...(creationContextParams?.dynamicUiSource?.fragmentAdf && {
						fragmentAdf: creationContextParams.dynamicUiSource.fragmentAdf,
					}),
				},
			}),
			...((isRemixAction ||
				source === 'remix' ||
				creationContextParams?.source === 'remix' ||
				isRemixEditContract) && {
				dynamicUiType:
					editorContext?.dynamicUiType ||
					creationContextParams?.dynamicUiType ||
					lastAssistantMessageDynamicUiType,
			}),
			...(editorContext?.dynamicUiSubtype && {
				dynamicUiSubtype: editorContext.dynamicUiSubtype,
			}),
			contentMauiId,
			mediaFileId,
		}),
		...(creationContextBlocks && { blocks: creationContextBlocks }),
		// Derive experience and contentTypes for CWR staging-area follow-ups and global chat context.
		...creationContextExperience,
		// Forms use their own skill routing for both initial prompts and refinements.
		...(isFormsSmartCreate && { experience: null, isStagingArea: false }),
	};

	// A remix edit targets a block, not the content hosting it, so the contract requires every
	// surface context to be absent. The context is still resolved above, because it carries the
	// MAUI identifiers that classify the edit in the first place.
	const sendsSurfaceContext = !isRemixEditContract;

	// Bookmark
	return {
		browserContext,
		editorContext: sendsSurfaceContext ? editorContext : undefined,
		whiteboardContext: sendsSurfaceContext ? whiteboardContext : undefined,
		slidesContext: sendsSurfaceContext ? slidesContext : undefined,
		databaseContext: sendsSurfaceContext ? databaseContext : undefined,
		dashboardEditorContext,
		additionalContext: {
			spaceCreateContext: await chatContextStoreActions.getSpaceCreateContext({
				isFirstPrompt,
				contentContext: contentContextValue,
			}),
			creationContext:
				isCreationContextContractEnabled || fg('confluence_aifc_new_live_page_chat_context')
					? applyCreationContextContract(creationContext)
					: creationContext,
			userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			search_artifact: searchArtifact,
		},
		computedExperience: isFormsSmartCreate ? undefined : creationContextExperience?.experience,
		aiFeatureContext: chatContextStoreActions.getAIFeatureContext(),
		jiraWorkflowContext: chatContextStoreActions.getJiraWorkflowContext(),
		dashboardContext: chatContextStoreActions.getDashboardContext(),
		useCurrentBrowsingContext: chatContextStoreActions.useCurrentBrowsingContext(
			!!addCurrentPageAsContextUserPreference,
		),
	};
};
